import {wrapError} from '@/lib/errors';
import {
  remoteConfig,
  fetchAndActivate,
  activate,
  getValue,
  setCustomSignals,
  onConfigUpdate,
} from './FirestoreService';

/**
 * Feature flag registry.
 *
 * ## How to add a new flag
 *
 * 1. Add an entry here with a `key` (matching the Remote Config parameter name) and a safe `default`.
 * 2. In the Firebase Remote Config console:
 *    a. Create a boolean parameter using the same key.
 *    b. Set the default value to `false`.
 *    c. Add one or more conditions to grant access per user tier, e.g.:
 *       - "Developers":    custom_signal('developer') == 'true'   - true
 *       - "Beta testers":  custom_signal('betaTester') == 'true'  - true
 *       - "All employees": custom_signal('npEmployee') == 'true'  - true
 * 3. Read the flag via `FlagService.getBooleanFlag(FLAGS.YOUR_FLAG)`.
 *
 * Custom signals (user roles) are set from Firebase Auth custom claims before each fetch,
 * so Remote Config evaluates conditions server-side and returns the right value per user tier.
 * You do NOT need to parse JSON or check claims manually for new flags.
 *
 * ## Local dev overrides (non-prod only)
 *
 * From the browser console:
 *
 *   FlagOverrides.set('show_devtools', true)
 *   FlagOverrides.set(FLAGS.SHOW_DEVTOOLS, false)
 *   FlagOverrides.clear('show_devtools')
 *   FlagOverrides.list()
 *   FlagOverrides.reset()
 *
 * Overrides are stored in `localStorage` under `'bmaps:flagOverrides'` and act as a
 * read-through bypass in `getBooleanFlag` (so `enforceFlag` inherits them too). A small
 * floating indicator appears in the corner while any override is active. Production
 * builds ignore overrides entirely and do not register `window.FlagOverrides`.
 *
 * ## Testing flagged features
 *
 * **Vitest** — mock the module with the shared factory and flip flags per `describe`. The
 * `vi.mock` factory uses a dynamic import because `vi.mock` is hoisted above the helper
 * import:
 *
 *   import {setFlag, clearFlags} from '$/utilities/flags';
 *   vi.mock('@/services/FlagService', async () => (await import('$/utilities/flags')).flagModuleMock());
 *   import {FLAGS} from '@/services/FlagService';
 *
 *   afterEach(clearFlags);
 *
 *   // TODO(flag-cleanup): when FLAG_UNDER_TEST is promoted, delete the "off"
 *   // describe and inline the "on" describe body.
 *   describe('FLAG_UNDER_TEST — on', () => {
 *     beforeEach(() => setFlag(FLAGS.FLAG_UNDER_TEST, true));
 *     // ...
 *   });
 *   describe('FLAG_UNDER_TEST — off', () => {
 *     beforeEach(() => setFlag(FLAGS.FLAG_UNDER_TEST, false));
 *     // ...
 *   });
 *
 * **Playwright** — import the flag fixture and scope overrides with `test.use({flags})`:
 *
 *   import {test, expect} from '$/e2e/fixtures/flagFixture';
 *
 *   // TODO(flag-cleanup): when FLAG_UNDER_TEST is promoted, delete the "off"
 *   // describe and remove test.use() from the "on" describe.
 *   test.describe('FLAG_UNDER_TEST — on', () => {
 *     test.use({flags: {FLAG_UNDER_TEST: true}});
 *     // ...
 *   });
 *   test.describe('FLAG_UNDER_TEST — off', () => {
 *     test.use({flags: {FLAG_UNDER_TEST: false}});
 *     // ...
 *   });
 *
 * The fixture seeds the same `localStorage` key the console overrides use, so the override
 * is in place before any page script runs and `getBooleanFlag` returns it on first read.
 *
 * ## Legacy JSON flags (getFlagValue)
 * Older flags store a JSON string like `{"developer": true, "betaTester": false}` in Remote Config
 * and parse it client-side. Do NOT use this pattern for new flags - it is being phased out.
 */
export const FLAGS = {
  SHOW_DEVTOOLS: {key: 'show_devtools', default: false},
  UNDERWRITING_DASHBOARD_ENABLED: {key: 'underwriting_dashboard_enabled', default: false},
  UNDERWRITING_V2_ENABLED: {key: 'underwriting_v2_enabled', default: false},
  ENABLE_SALESFORCE_PROJECT_CREATE: {key: 'enable_salesforce_project_create', default: false},
  ENABLE_LABELS_V2: {key: 'enable_labels_v2', default: false},
  MERGE_COMPS_ENABLED: {key: 'merge_comps_enabled', default: false},
} as const satisfies Record<string, {key: string; default: boolean}>;

const roles = [
  'npEmployee',
  'betaTester',
  'developer',
];

export const OVERRIDE_STORAGE_KEY = 'bmaps:flagOverrides';
export const OVERRIDE_CHANGE_EVENT = 'flag-overrides-change';
const isProd = process.env.NODE_ENV === 'production';

export const readOverrides = (): Record<string, boolean> => {
  if (isProd) return {};
  try {
    const parsed = JSON.parse(localStorage.getItem(OVERRIDE_STORAGE_KEY) ?? '{}');
    return (parsed && typeof parsed === 'object') ? parsed : {};
  } catch {
    return {};
  }
};

const writeOverrides = (next: Record<string, boolean>) => {
  if (isProd) return;
  localStorage.setItem(OVERRIDE_STORAGE_KEY, JSON.stringify(next));
  window.dispatchEvent(new CustomEvent(OVERRIDE_CHANGE_EVENT));
};

const resolveFlagKey = (keyOrFlag: string | {key: string}): string => (
  typeof keyOrFlag === 'string' ? keyOrFlag : keyOrFlag.key
);

const registerFlagOverridesConsoleApi = () => {
  if (isProd) return;
  (window as unknown as {FlagOverrides: unknown}).FlagOverrides = {
    set(keyOrFlag: string | {key: string}, value: boolean) {
      const key = resolveFlagKey(keyOrFlag);
      const next = {...readOverrides(), [key]: !!value};
      writeOverrides(next);
      console.log(`[FlagOverrides] set ${key} = ${!!value}`);
    },
    clear(keyOrFlag: string | {key: string}) {
      const key = resolveFlagKey(keyOrFlag);
      const next = {...readOverrides()};
      delete next[key];
      writeOverrides(next);
      console.log(`[FlagOverrides] cleared ${key}`);
    },
    list() {
      const current = readOverrides();
      console.log('[FlagOverrides] current overrides:', current);
      return current;
    },
    reset() {
      writeOverrides({});
      console.log('[FlagOverrides] cleared all overrides');
    },
    // expose flag registry for easy reference in console, e.g. FlagOverrides.set(FLAGS.SHOW_DEVTOOLS, true)
    FLAGS,
  };
};

class FlagService {
  claims: Record<string, string>;

  constructor(claims: Record<string, string>) {
    this.claims = claims;
  }

  public async initializeRemoteConfig() {
    // Skip remote config initialization for E2E tests
    if (window['PLAYWRIGHT_FIREBASE_CONFIG']?.emulators) return false;

    try {
      if (!(remoteConfig && typeof remoteConfig === 'object')) {
        console.warn('remoteConfig is not available; skipping fetchAndActivate');
        return false;
      }
      if (!remoteConfig.settings) {
        remoteConfig.settings = {
          // Real-time updates (onConfigUpdate) are the primary freshness mechanism;
          // the periodic fetch is just a fallback, so a longer interval is fine.
          minimumFetchIntervalMillis: 3_600_000, // 1 hour
          fetchTimeoutMillis: 60_000, // 1 minute
        };
      }

      remoteConfig.settings.minimumFetchIntervalMillis = 3_600_000; // 1 hour

      // Set explicit in-app defaults so flags return correct values before the first fetch.
      remoteConfig.defaultConfig = Object.fromEntries(
        Object.values(FLAGS).map(({key, default: def}) => [key, def]),
      );

      // Forward auth custom claims as Remote Config custom signals before fetching.
      // This lets Remote Config evaluate tier-based conditions (developer, betaTester,
      // npEmployee) server-side and return the correct value per user without client-side
      // JSON parsing. Signals must be set before fetchAndActivate.
      await setCustomSignals(remoteConfig, {
        developer: String(Boolean(this.claims.developer)),
        betaTester: String(Boolean(this.claims.betaTester)),
        npEmployee: String(Boolean(this.claims.npEmployee)),
      });

      const activated = await fetchAndActivate(remoteConfig);
      this.subscribeToConfigUpdates();
      return activated;
    } catch (err) {
      console.warn('Failed to initialize remote config, continuing without it', err);
    }
    return false;
  }

  /**
   * Subscribes to real-time Remote Config updates so published flag changes activate within
   * seconds instead of waiting for the periodic fetch. Tears down any prior subscription first
   * (initialize() runs again on each auth change), so only one stream is ever open.
   *
   * Does not force a re-render of already-mounted components, which pick up the new value on their next read.
   */
  private subscribeToConfigUpdates() {
    unsubscribeConfigUpdates?.();
    unsubscribeConfigUpdates = onConfigUpdate(remoteConfig, {
      next: (configUpdate) => {
        try {
          activate(remoteConfig);
          console.info('Remote Config updated', [...configUpdate.getUpdatedKeys()]);
        } catch (err) {
          console.warn('Failed to activate real-time Remote Config update', err);
        }
      },
      error: (err) => {
        // Hides warnings that get generated from switching tabs
        if (document.hidden) return;
        console.warn('Remote Config real-time stream error', err);
      },
      complete: () => {},
    });
  }

  /**
   * Gets a boolean flag. Use this for all new flags.
   * Role-based access is configured via conditions in the Remote Config console
   * using the custom signals set during initialization.
   */
  public getBooleanFlag(flag: {key: string; default: boolean}): boolean {
    const overrides = readOverrides();
    if (flag.key in overrides) return overrides[flag.key];
    return getValue(remoteConfig, flag.key).asBoolean();
  }

  /**
   * @deprecated Use getBooleanFlag with Remote Config conditions instead.
   * Gets a role-based flag stored as a JSON string in Remote Config.
   * Parses the JSON and returns true if any of the user's roles are enabled.
   */
  public getFlagValue(flagName: string): boolean {
    const value = getValue(remoteConfig, flagName).asString();
    if (value == null || value === '') {
      console.error(`Flag ${flagName} not found`);
      return false;
    }

    let json;
    try {
      json = JSON.parse(value);
    } catch (error) {
      console.error(wrapError(error, `Flag ${flagName} is not a valid JSON string`));
      return false;
    }

    let result = false;
    for (let i = 0; i < roles.length; i++) {
      const role = roles[i];
      if (json[role] && this.claims[role]) {
        result = true;
        break;
      }
    }
    return result;
  }

  /**
   * Executes one of two code paths based on a feature flag, making cleanup explicit.
   *
   * Prefer this over a plain `if (FlagService.getBooleanFlag(...))` when both branches do
   * meaningful work. The two-callback structure makes it obvious which branch to delete when
   * the flag is removed: delete the `ifEnabled` callback (and the `enforceFlag` wrapper) to
   * permanently disable, or inline `ifEnabled` and delete `ifDisabled` to permanently enable.
   *
   * **Not a good fit** for additive patterns where the flag only guards a side-effect and
   * execution continues with shared code afterward - a commented `if` block is clearer there.
   *
   * **Async usage**: if either callback is async, `await` the result at the call site.
   * TypeScript will surface the `Promise` in the inferred return type, but won't error if
   * you forget the `await`.
   *
   * @example
   * return FlagService.enforceFlag(FLAGS.UNDERWRITING_V2_ENABLED, {
   *   ifEnabled: () => renderDashboardV2(),
   *   ifDisabled: () => renderDashboardV1(),
   * });
   */
  enforceFlag<TEnabled, TDisabled>(
    flag: {key: string; default: boolean},
    {ifEnabled, ifDisabled, additionalConditions}: {
      ifEnabled: () => TEnabled;
      ifDisabled?: () => TDisabled;
      additionalConditions?: () => boolean;
    },
  ): TEnabled | TDisabled | undefined {
    if (this.getBooleanFlag(flag) && (additionalConditions?.() ?? true)) {
      return ifEnabled();
    }
    return ifDisabled?.();
  }
}

let service: FlagService;
let unsubscribeConfigUpdates: (() => void) | null = null;
let resolveReady!: () => void;
const ready: Promise<void> = new Promise((resolve) => {
  resolveReady = resolve;
});

const initialize = async (claims: Record<string, string>) => {
  service = new FlagService(claims);
  registerFlagOverridesConsoleApi();
  await service.initializeRemoteConfig();
  resolveReady();
};

export {
  service as FlagService,
  ready,
  initialize,
};
